import styled from '@emotion/styled';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { tokens } from '@hotjar/hotsauce';
import { Layer, processScrollMarkers } from '@hotjar/heatmaps';

const MarkerContainer = Layer.withComponent(styled.div`
  overflow: hidden;
  position: relative;
`);

const Marker = Layer.withComponent(styled.div<{ relativePosition: number }>`
  box-shadow: 0 3px 5px 0 rgba(50, 50, 50, 0.25);
  font-size: 11px;
  font-weight: bold;
  position: relative;
  text-transform: uppercase;
  top: ${({ relativePosition }) =>
    `calc(${[
      `${relativePosition}%`, // initial value
      `1em`, // relative font height
      `8px`, // total vertical padding
      `2px`, // height of horizontal marker
    ].join(' - ')})`};
  width: 100%;

  :after {
    border-bottom: 2px solid white;
    bottom: 0;
    content: '';
    display: block;
    left: 0;
    position: absolute;
    width: 100%;
  }
`);

const MarkerText = styled.span`
  background: ${tokens.color.ui_background};
  border-radius: 0 ${tokens.radius.m} 0 0;
  color: ${tokens.color.text_default};
  display: inline-block;
  padding: ${tokens.spacer.x0} ${tokens.spacer.x1};
`;

const FixedMarker = styled(Marker)`
  opacity: 0.65;
  :after {
    border-bottom: 1px dotted ${tokens.color.neutral_800};
  }
`;

const FixedMarkerText = styled(MarkerText)`
  background: ${tokens.color.neutral_800};
  color: ${tokens.color.white};
`;

const ReachMarker = styled.div`
  text-align: center;
  position: absolute;
  width: 100%;
  opacity: 0.7;
  ${tokens.transition('opacity')};

  &.hide {
    opacity: 0;
  }

  :after {
    content: '';
    border-bottom: 1px solid ${tokens.color.neutral_800};
    display: block;
    margin: calc(-0.5em - ${tokens.spacer.x0});
  }
`;

const ReachMarkerContent = styled.span`
  cursor: default;
  background: ${tokens.color.neutral_800};
  border-radius: ${tokens.radius.m};
  padding: ${tokens.spacer.x0} ${tokens.spacer.x1};
  color: ${tokens.color.white};
`;

interface ScrollMarkersLayerProps {
  heatmapData: HeatmapAPI.GetScrollHeatmapData;
}

export const ScrollMarkersLayer = ({ heatmapData }: ScrollMarkersLayerProps): JSX.Element => {
  const { t } = useTranslation('heatmaps');
  const {
    averageWindowHeight,
    imageSize: [, imageHeight],
  } = heatmapData;

  const relativePosition = (averageWindowHeight / imageHeight) * 100;
  const scrollReachFixedMarkers = processScrollMarkers(imageHeight, heatmapData.scrollReach);
  const ref = useRef<HTMLDivElement>(null);
  const mouseMoveNode = ref.current;
  const [position, setPosition] = useState(0);

  const setFromEvent = (e: MouseEvent) => {
    const { top, height } = (ref.current as HTMLDivElement).getBoundingClientRect();
    const { clientY } = e;
    const y = clientY - top;

    setPosition((y / height) * 100);
  };

  useEffect(() => {
    const node = ref.current;
    if (node) {
      node.addEventListener('mousemove', setFromEvent);
    }
    return () => {
      if (node) {
        node.removeEventListener('mousemove', setFromEvent);
      }
    };
  }, [mouseMoveNode]);

  return (
    <MarkerContainer ref={ref}>
      <Marker relativePosition={relativePosition}>
        <MarkerText>{t('scroll-markers.average-fold')}</MarkerText>
      </Marker>

      {scrollReachFixedMarkers.map(({ top, value }) => {
        return (
          <FixedMarker key={value} relativePosition={top}>
            <FixedMarkerText>{value}</FixedMarkerText>
          </FixedMarker>
        );
      })}

      {position > 0 && (
        <ReachMarker style={{ top: `${position}%` }}>
          <ReachMarkerContent>
            {heatmapData.scrollReach[
              Math.round(heatmapData.scrollReach.length * (position / 100))
            ] / 10}
            {t('scroll-markers.reach')}
          </ReachMarkerContent>
        </ReachMarker>
      )}
    </MarkerContainer>
  );
};
